import type {
    AlabamaReportOptionsValues,
    ArizonaReportOptionsValues,
    GeorgiaReportOptionsValues,
    MichiganReportOptionsValues,
    NevadaReportOptionsValues,
    NorthCarolinaReportOptionsValues,
    PennsylvaniaReportOptionsValues,
    TexasReportOptionsValues,
} from "@/pages/pac/filings/components/types"
import type { PACCReportRequest } from "@/types/djangio"
import type { QueryFunctionContext } from "@tanstack/react-query"
import { useMutation, useQuery } from "@tanstack/react-query"
import { api } from "@/api"

import axios from "axios"

export type ReportOptionsValues = AlabamaReportOptionsValues &
    ArizonaReportOptionsValues &
    GeorgiaReportOptionsValues &
    MichiganReportOptionsValues &
    NevadaReportOptionsValues &
    NorthCarolinaReportOptionsValues &
    PennsylvaniaReportOptionsValues &
    TexasReportOptionsValues

type FileData = void | { download: string } | { report_type: string } | { filing_version: string }

type PacReportRequestData = {
    file?: FileData
    options?: unknown
    pac_file_id: number
    report_type: string
    action: number
}

type LatestPacFileData = {
    succeeded: boolean
    most_recent_efile_id: number
    most_recent_as_filed_pdf_id: number
    most_recent_federal_id: number
    user_full_name: string
    created: string
}

export const paccReportRequestQueryKeys = {
    all: [{ _model: "paccreportrequest" }] as const,
    in_progress_generation: (pacFileId: string) =>
        [{ ...paccReportRequestQueryKeys.all[0], pacFileId }, "in_progress"] as const,
    completed_file_generations: (pacFileId: string) =>
        [{ ...paccReportRequestQueryKeys.all[0], pacFileId }, "completed"] as const,
}

const fetchInProgressFileGeneration = async ({
    queryKey,
}: QueryFunctionContext<
    ReturnType<(typeof paccReportRequestQueryKeys)["in_progress_generation"]>
>): Promise<PACCReportRequest> => {
    const [{ pacFileId }] = queryKey

    const response = await (DjangIO.app.pac.state_pac.models.pacc_report_request.PACCReportRequest as any).objects
        .filter({
            pac_file_id: Number(pacFileId),
            action__in: [
                DjangIO.app.pac.state_pac.types.PACCReportActionType.actually_file.value,
                DjangIO.app.pac.state_pac.types.PACCReportActionType.generate_federal.value,
            ],
            report_status__in: [
                DjangIO.app.pac.state_pac.types.PACCReportStatus.requested.value,
                DjangIO.app.pac.state_pac.types.PACCReportStatus.generating.value,
            ],
        })
        .limit(1)
        .GET()

    return response
}

export const useInProgressFileGeneration = (pacFileId: string) =>
    useQuery({
        queryKey: paccReportRequestQueryKeys.in_progress_generation(pacFileId),
        queryFn: fetchInProgressFileGeneration,
        enabled: Boolean(pacFileId),
    }) as any

const fetchCompletedFileGenerations = async ({
    queryKey,
}: QueryFunctionContext<
    ReturnType<(typeof paccReportRequestQueryKeys)["completed_file_generations"]>
>): Promise<LatestPacFileData> => {
    const [{ pacFileId }] = queryKey

    const response = await api.get({
        model: DjangIO.app.pac.state_pac.models.pacc_report_request.PACCReportRequest,
        action: "completed_file_generations",
        params: {
            pac_file_id: pacFileId,
        },
    })

    const parsedResponse = await response.json()
    return parsedResponse
}

export const useCompletedFileGenerations = (pacFileId: string) =>
    useQuery({
        queryKey: paccReportRequestQueryKeys.completed_file_generations(pacFileId),
        queryFn: fetchCompletedFileGenerations,
        enabled: Boolean(pacFileId),
        select: (data) => {
            return {
                created: data.created,
                mostRecentAsFiledPdfId: data.most_recent_as_filed_pdf_id,
                mostRecentEfileId: data.most_recent_efile_id,
                mostRecentFederalId: data.most_recent_federal_id,
                succeeded: data.succeeded,
                userFullName: data.user_full_name,
            }
        },
    }) as any

// Payload to PAC Classic trigger the job with the selected options
const getOptionsByJurisdiction = (jurisdictionKey: string) => {
    switch (jurisdictionKey) {
        case "alabama":
            return (values: AlabamaReportOptionsValues) => {
                return {
                    summaryPage: values.options.includes("summaryPage"),
                    form2: values.options.includes("form2"),
                    form3: values.options.includes("form3"),
                    form4: values.options.includes("form4"),
                    form5: values.options.includes("form5"),
                }
            }
        case "arizona":
            return (values: ArizonaReportOptionsValues) => {
                return {
                    cover_sheet: values.options.includes("coverSheet"),
                    summary_page: values.options.includes("summaryPage"),
                    schedule_a: values.options.includes("scheduleA"),
                    schedule_a1: values.options.includes("scheduleA1"),
                    schedule_b: values.options.includes("scheduleB"),
                    schedule_c1: values.options.includes("scheduleC1"),
                    schedule_c2: values.options.includes("scheduleC2"),
                    schedule_c2_start_page: values.scheduleC2StartPage,
                    schedule_c2_end_page: values.scheduleC2EndPage,
                    schedule_c3a: values.options.includes("scheduleC3a"),
                    schedule_c3b: values.options.includes("scheduleC3b"),
                    schedule_c3c: values.options.includes("scheduleC3c"),
                    schedule_c4a: values.options.includes("scheduleC4a"),
                    schedule_c4b: values.options.includes("scheduleC4b"),
                    schedule_c4c: values.options.includes("scheduleC4c"),
                    schedule_c5: values.options.includes("scheduleC5"),
                    schedule_c5a: values.options.includes("scheduleC5a"),
                    schedule_c8: values.options.includes("scheduleC8"),
                    schedule_d: values.options.includes("scheduleD"),
                    schedule_d1: values.options.includes("scheduleD1"),
                    schedule_d2: values.options.includes("scheduleD2"),
                    schedule_d3: values.options.includes("scheduleD3"),
                    schedule_d5: values.options.includes("scheduleD5"),
                    schedule_d6: values.options.includes("scheduleD6"),
                    schedule_d7: values.options.includes("scheduleD7"),
                    schedule_e: values.options.includes("scheduleE"),
                    schedule_e1: values.options.includes("scheduleE1"),
                    schedule_e2a: values.options.includes("scheduleE2a"),
                    schedule_e2b: values.options.includes("scheduleE2b"),
                    schedule_e2c: values.options.includes("scheduleE2c"),
                    schedule_e3a: values.options.includes("scheduleE3a"),
                    schedule_e3b: values.options.includes("scheduleE3b"),
                    schedule_e3c: values.options.includes("scheduleE3c"),
                    schedule_e3d: values.options.includes("scheduleE3d"),
                    schedule_e3e: values.options.includes("scheduleE3e"),
                    schedule_e3f: values.options.includes("scheduleE3f"),
                    schedule_e4: values.options.includes("scheduleE4"),
                    schedule_f1: values.options.includes("scheduleF1"),
                    schedule_f2: values.options.includes("scheduleF2"),
                    schedule_l1: values.options.includes("scheduleL1"),
                    schedule_l2: values.options.includes("scheduleL2"),
                    schedule_r1: values.options.includes("scheduleR1"),
                    schedule_t1: values.options.includes("scheduleT1"),
                }
            }
        case "georgia":
            return (values: GeorgiaReportOptionsValues) => {
                return {
                    contributions_received: values.options.includes("contributionsReceived"),
                    expenditures: values.options.includes("expenditures"),
                    investments: values.options.includes("investments"),
                    loans: values.options.includes("loans"),
                }
            }
        case "michigan":
            return (values: MichiganReportOptionsValues) => {
                return {
                    cover_page: values.options.includes("coverPage"),
                    summary_page: values.options.includes("summaryPage"),
                    schedule_2a: values.options.includes("schedule2a"),
                    schedule_2a1: values.options.includes("schedule2a1"),
                    schedule_2a2: values.options.includes("schedule2a2"),
                    schedule_2ik: values.options.includes("schedule2ik"),
                    schedule_2b: values.options.includes("schedule2b"),
                    schedule_2b1: values.options.includes("schedule2b1"),
                    schedule_2b2: values.options.includes("schedule2b2"),
                    schedule_2e: values.options.includes("schedule2e"),
                    schedule_2f: values.options.includes("schedule2f"),
                    schedule_bg: values.options.includes("scheduleBg"),
                }
            }
        case "nevada":
            return (values: NevadaReportOptionsValues) => {
                return {
                    summary_page: values.options.includes("summaryPage"),
                    campaign_contributions: values.options.includes("campaignContributions"),
                    campaign_expenses: values.options.includes("campaignExpenses"),
                    inkind_campaign_contributions: values.options.includes("inkindCampaignContributions"),
                    inkind_campaign_expenses: values.options.includes("inkindCampaignExpenses"),
                    written_commitments: values.options.includes("writtenCommitments"),
                    inkind_written_commitments: values.options.includes("inkindWrittenCommitments"),
                }
            }
        case "north_carolina":
            return (values: NorthCarolinaReportOptionsValues) => {
                return {
                    admin_support: values.options.includes("adminSupport"),
                    aggregated_contributions_from_individuals_form_cro_1205: values.options.includes(
                        "aggregatedContributionsFromIndividualsFormCRO1205",
                    ),
                    contributions_from_individuals_form_cro_1210: values.options.includes(
                        "contributionsFromIndividualsFormCRO1210",
                    ),
                    cover_page: values.options.includes("coverPage"),
                    end_page_form_cro_1205: values.endPageFormCRO1205,
                    end_page_form_cro_1210: values.endPageFormCRO1210,
                    itemized_disbursements: values.options.includes("itemizedDisbursements"),
                    loan_proceeds: values.options.includes("loanProceeds"),
                    loan_repayments: values.options.includes("loanRepayments"),
                    other_contributions_and_receipts: values.options.includes("otherContributionsAndReceipts"),
                    start_page_form_cro_1205: values.startPageFormCRO1205,
                    start_page_form_cro_1210: values.startPageFormCRO1210,
                    summary_page: values.options.includes("summaryPage"),
                }
            }
        case "pennsylvania":
            return (values: PennsylvaniaReportOptionsValues) => {
                return {
                    schedule_cover_sheet: values.options.includes("scheduleCoverSheet"),
                    schedule_1: values.options.includes("schedule1"),
                    schedule_1_start_page: values.schedule1StartPage,
                    schedule_1_end_page: values.schedule1EndPage,
                    schedule_2: values.options.includes("schedule2"),
                    schedule_3: values.options.includes("schedule3"),
                    schedule_4: values.options.includes("schedule4"),
                }
            }
        case "texas":
            return (values: TexasReportOptionsValues) => {
                return {
                    schedule_PACT: false,
                    schedule_cover_sheet_page_1: values.options.includes("scheduleCoverSheetPage1"),
                    schedule_cover_sheet_page_2: values.options.includes("scheduleCoverSheetPage2"),
                    schedule_A2: values.options.includes("scheduleA2"),
                    schedule_B2: values.options.includes("scheduleB2"),
                    schedule_C: values.options.includes("scheduleC"),
                    schedule_E: values.options.includes("scheduleE"),
                    schedule_F: values.options.includes("scheduleF"),
                    schedule_I: values.options.includes("scheduleI"),
                    schedule_J: values.options.includes("scheduleJ"),
                    schedule_K: values.options.includes("scheduleK"),
                    schedule_T: values.options.includes("scheduleT"),
                    dissolution_page: values.options.includes("dissolutionPage"),
                }
            }
        default:
            return () => {
                // Deliberately return nothing
            }
    }
}

// User ability to choose wheter he wants his report as a ZIP, PDF or CSV.
// The user also can choose if he wants to download the current or original filing.
const getValuesByJurisdiction = (jurisdictionKey: string) => {
    switch (jurisdictionKey) {
        case "alabama":
            return () => {
                return {
                    // Fixed default value on report_type for generating the file ( Might need to change )
                    report_type: "pdf",
                }
            }
        case "arizona":
            return (values: ArizonaReportOptionsValues) => {
                return {
                    report_type: values.reportType,
                }
            }
        case "georgia":
            return (values: GeorgiaReportOptionsValues) => {
                return {
                    // Fixed default value on report_type for generating the file ( Might need to change )
                    report_type: "pdf",
                    download: values.download,
                }
            }
        case "michigan":
            return (values: MichiganReportOptionsValues) => {
                return {
                    report_type: values.reportType,
                }
            }
        case "nevada":
            return (values: NevadaReportOptionsValues) => {
                return {
                    download: values.download,
                    report_type: values.reportType,
                }
            }
        case "north_carolina":
            return (values: NorthCarolinaReportOptionsValues) => {
                return {
                    download: values.download,
                    report_type: values.reportType,
                }
            }
        case "pennsylvania":
            return (values: PennsylvaniaReportOptionsValues) => {
                return {
                    report_type: values.reportType,
                }
            }
        case "texas":
            return (values: TexasReportOptionsValues) => {
                return {
                    report_type: values.filingVersion,
                }
            }
        default:
            return () => {
                // Deliberately return nothing
            }
    }
}

export const useMutateGeneratedFile = (
    filingsId: string,
    jurisdictionKey: string,
    isGenerateReport?: boolean,
    isPreviewReport?: boolean,
) =>
    useMutation({
        mutationFn: async (values: ReportOptionsValues) => {
            const data: PacReportRequestData = {
                pac_file_id: Number(filingsId),
                report_type: undefined,
                action: undefined,
            }

            // Generate the preview of a report
            if (isPreviewReport) {
                data.report_type = getValuesByJurisdiction(jurisdictionKey)(values)["report_type"]
                data.options = getOptionsByJurisdiction(jurisdictionKey)(values)
                // TODO: We need to understand how PAC Classic is receiving this values.
                // Because the backend is not sending anything related to "file"
                data.file = getValuesByJurisdiction(jurisdictionKey)(values)
                data.action = DjangIO.app.pac.state_pac.types.PACCReportActionType.for_review.value
            }

            // Generate the report and file on the system
            if (isGenerateReport) {
                data.report_type = "zip"
                data.action = DjangIO.app.pac.state_pac.types.PACCReportActionType.actually_file.value
            }

            return axios.post("/api/pacc-report-request/generate/", data)
        },
    })
